import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import ChecklistProntidao from '@/components/ChecklistProntidao'
import { trackCustomEvent } from '@/config/analytics'
import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck,
  CalendarX,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  HeartHandshake,
  MessageSquareHeart,
  ShieldCheck,
  Stethoscope,
} from 'lucide-react'
import heroPaciente from '@/assets/cta-acolhimento.png'
import examesOrganizados from '@/assets/exames-organizados.png'
import concierge from '@/assets/concierge.png'
import ctaAcolhimento from '@/assets/cta-acolhimento.png'
import fotoCancelamento from '@/assets/foto-cancelamento.png'

const WHATSAPP_NUMBER = '5585982286045'
const canonicalUrl = 'https://www.cirurgiaautorizada.com.br/consultoria-pre-cirurgica'

const dores = [
  {
    icon: Stethoscope,
    title: 'Insegurança após a indicação',
    description:
      'Você recebeu a indicação, mas ainda está tentando entender riscos e próximos passos.',
  },
  {
    icon: FileText,
    title: 'Exames e documentos espalhados',
    description:
      'Laudos, pedidos, guias e senhas ficam descentralizados. Sentiu o medo de esquecer algo?',
  },
  {
    icon: ClipboardCheck,
    title: 'Autorizações travadas',
    description:
      'Nem sempre está claro o que já foi solicitado, o que foi negado ou o que ainda falta.',
  },
  {
    icon: CalendarCheck,
    title: 'Preparo do dia indefinido',
    description:
      'Jejum, horário, acompanhante e itens da bolsa geram dúvidas que se acumulam.',
  },
]

const riscosPreparacao = [
  {
    icon: CalendarX,
    title: 'O trauma do cancelamento',
    description:
      'Imagine a cena: você e seu familiar se preparam psicologicamente, chegam ao hospital e a cirurgia é suspensa na porta do centro cirúrgico por um jejum feito incorretamente ou pela falta de um exame. O desgaste emocional de ter que passar por toda a expectativa e ansiedade novamente é devastador para quem você ama.',
  },
  {
    icon: AlertTriangle,
    title: 'Riscos reais à saúde',
    description:
      'A falta de um alinhamento rigoroso sobre o histórico clínico como, por exemplo, o tempo correto de interrupção de medicações contínuas (anticoagulantes ou canetas emagrecedoras), pode gerar complicações graves na mesa de cirurgia.',
  },
  {
    icon: HeartHandshake,
    title: 'A sobrecarga de quem cuida',
    description:
      'Tentar resolver burocracias com o plano de saúde por telefone, andando pelos corredores do hospital, enquanto você deveria estar dando suporte emocional e carinho para o seu ente querido. Ninguém merece carregar esse peso sozinho.',
  },
]

const beneficios = [
  {
    icon: MessageSquareHeart,
    title: 'Suporte dedicado ao paciente',
    description:
      'Você não atravessa o pré-operatório sozinho. Ajudamos a organizar dúvidas e prioridades.',
    bullets: ['Atendimento acolhedor', 'Orientação prática', 'Comunicação clara com a equipe'],
  },
  {
    icon: ClipboardCheck,
    title: 'Avaliação estruturada',
    description:
      'Transformamos a confusão em um mapa objetivo do que está pronto e do que falta.',
    bullets: ['Checklist guiado', 'Leitura por categoria', 'Relatório imediato'],
  },
  {
    icon: HeartHandshake,
    title: 'Concierge pré-cirúrgico',
    description:
      'Alinhamos preparação, documentação e logística para você chegar à cirurgia tranquilo.',
    bullets: ['Revisão dos pontos de atenção', 'Apoio com documentos', 'Eliminação de surpresas'],
  },
]

const passos = [
  {
    step: '01',
    title: 'Faça o checklist gratuito',
    description:
      'Em poucos cliques você identifica onde estão seus principais pontos de atenção.',
  },
  {
    step: '02',
    title: 'Receba seu relatório na hora',
    description:
      'Entenda seu nível de preparação e veja quais detalhes revisar.',
  },
  {
    step: '03',
    title: 'Contrate o concierge',
    description:
      'Nossa equipe revisa seu caso e orienta a melhor forma de organizar o caminho.',
  },
]

const faqs = [
  {
    value: 'faq-1',
    question: 'Para quem é essa consultoria pré-cirúrgica?',
    answer:
      'Para pacientes que já receberam indicação cirúrgica e estão inseguros com exames, autorizações, documentos, preparo ou logística até o dia da cirurgia.',
  },
  {
    value: 'faq-2',
    question: 'O checklist substitui uma consulta médica?',
    answer:
      'Não. É uma ferramenta educativa e organizacional. Ajuda a identificar lacunas na preparação, mas não substitui avaliação médica, anestésica ou hospitalar.',
  },
  {
    value: 'faq-3',
    question: 'Posso fazer o checklist mesmo sem plano de saúde?',
    answer:
      'Sim. O relatório ajuda tanto quem vai operar com convênio quanto quem está em fluxo particular, porque aborda preparo clínico, documentos e logística.',
  },
  {
    value: 'faq-4',
    question: 'O que acontece depois da avaliação gratuita?',
    answer:
      'Nosso questionário ajudará você a entender em que momento de preparação você está e como nossa equipe pode ter ajudar.',
  },
  {
    value: 'faq-5',
    question: 'Preciso contratar para ver meu resultado?',
    answer:
      'Não. O relatório é liberado gratuitamente. A consultoria é opcional e pode ser avaliada depois, se fizer sentido para você.',
  },
]

function abrirWhatsAppInicial() {
  const message = encodeURIComponent(
    'Olá! Tenho interesse na Consultoria Pré-Cirúrgica e gostaria de entender como vocês podem me ajudar.'
  )
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank')
}

function scrollToChecklist() {
  document.getElementById('checklist-prontidao')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const ConsultoriaPreCirurgica = () => {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Consultoria Pré-Cirúrgica',
    provider: {
      '@type': 'Organization',
      name: 'Cirurgia Autorizada',
      url: 'https://www.cirurgiaautorizada.com.br',
    },
    serviceType: 'Consultoria e orientação pré-cirúrgica',
    areaServed: 'BR',
    description:
      'Apoio para pacientes com indicação cirúrgica que precisam organizar exames, autorizações, documentos e preparo até o dia da cirurgia.',
    url: canonicalUrl,
  }

  const handleHeroChecklistClick = () => {
    trackCustomEvent('hero_checklist_click', 'button', 'consultoria_pre_cirurgica')
    scrollToChecklist()
  }

  const handleHeroWhatsAppClick = () => {
    trackCustomEvent('hero_whatsapp_click', 'button', 'consultoria_pre_cirurgica')
    trackCustomEvent('concierge_request', 'button', 'falar_com_concierge')
    abrirWhatsAppInicial()
  }

  const handleFinalChecklistClick = () => {
    trackCustomEvent('final_checklist_click', 'button', 'consultoria_pre_cirurgica')
    scrollToChecklist()
  }

  const handleFinalWhatsAppClick = () => {
    trackCustomEvent('final_whatsapp_click', 'button', 'consultoria_pre_cirurgica')
    trackCustomEvent('concierge_request', 'button', 'falar_com_concierge')
    abrirWhatsAppInicial()
  }

  return (
    <>
      <title>Consultoria Pré-Cirúrgica | Cirurgia Autorizada</title>
      <meta
        name="description"
        content="Recebeu indicação cirúrgica? Faça o Checklist de Prontidão gratuito e organize exames, autorizações e preparo com apoio do nosso concierge."
      />
      <meta name="robots" content="index,follow" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Consultoria Pré-Cirúrgica | Cirurgia Autorizada" />
      <meta
        property="og:description"
        content="Faça seu Checklist de Prontidão para Cirurgia e receba orientação clara antes do procedimento."
      />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Cirurgia Autorizada" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Consultoria Pré-Cirúrgica | Cirurgia Autorizada" />
      <meta
        name="twitter:description"
        content="Avalie exames, documentos, autorizações e preparo com um checklist gratuito e receba orientação clara para sua cirurgia."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
      />

      <div>
        <section className="relative min-h-[680px] lg:min-h-[720px] flex items-center overflow-hidden">
          <img
            src={heroPaciente}
            alt="Acolhimento em ambiente hospitalar sereno"
            width={1600}
            height={1200}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/75 to-navy/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />

          <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center w-full">
            <div className="space-y-8 text-cream">
              <span className="inline-flex items-center gap-2 rounded-full bg-cream/10 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-wider font-medium border border-cream/15">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-soft" />
                Apoio para pacientes com indicação cirúrgica
              </span>

              <h1 className="font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                Recebeu indicação de cirurgia e{' '}
                <em className="text-teal-soft not-italic">não sabe por onde começar?</em>
              </h1>

              <p className="text-lg md:text-xl text-cream/85 max-w-xl leading-relaxed">
                Se você está inseguro com exames, autorizações e preparo, a Consultoria
                Pré-Cirúrgica organiza o caminho e elimina surpresas antes do procedimento.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={handleHeroChecklistClick}
                  className="bg-teal hover:bg-teal-deep text-white h-14 px-8 text-base rounded-full shadow-lift"
                >
                  Fazer meu checklist gratuito
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleHeroWhatsAppClick}
                  className="h-14 px-8 text-base rounded-full bg-cream/5 backdrop-blur border-cream/25 text-cream hover:bg-cream/15 hover:text-cream"
                >
                  Falar com o concierge
                </Button>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream/75 pt-2">
                {['Relatório imediato', 'Gratuito e sem compromisso', 'Dados protegidos'].map(
                  (t) => (
                    <span key={t} className="inline-flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-teal-soft" />
                      {t}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-6 bg-teal/20 blur-3xl rounded-[3rem]" />
                <Card className="relative bg-cream/10 backdrop-blur-md border border-cream/20 text-cream rounded-3xl shadow-lift">
                  <CardContent className="p-8 space-y-5">
                    <div className="inline-flex items-center gap-2 rounded-full bg-teal-soft/20 px-3 py-1 text-xs text-teal-soft">
                      <ClipboardCheck className="h-3.5 w-3.5" />
                      Em 2 minutos você descobre
                    </div>
                    <h2 className="font-bold text-2xl leading-snug">
                      Se você está preparado em cada frente antes da cirurgia
                    </h2>
                    <ul className="space-y-3.5">
                      {[
                        'Exames e resultados acessíveis',
                        'Documentos e autorizações organizados',
                        'Histórico clínico e medicações alinhados',
                        'Preparo do dia da cirurgia sob controle',
                      ].map((item) => (
                        <li key={item} className="flex gap-3 text-cream/95">
                          <CheckCircle2 className="h-5 w-5 text-teal-soft mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 relative">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
                  Se isso parece familiar
                </span>
                <h2 className="font-bold text-3xl md:text-5xl text-navy mt-3 leading-tight">
                  Esta página foi feita para você
                </h2>
                <p className="text-ink-soft text-lg mt-4 max-w-xl">
                  Muitos pacientes chegam à fase pré-cirúrgica com a decisão clínica tomada, mas
                  sem clareza operacional. É aí que a orientação faz diferença.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {dores.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="group rounded-2xl bg-card border border-border p-5 hover:border-teal transition-colors"
                  >
                    <div className="h-10 w-10 rounded-xl bg-teal/10 text-teal-deep flex items-center justify-center mb-4 group-hover:bg-teal group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-medium text-navy mb-1.5">{title}</h3>
                    <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <span className="text-xs uppercase tracking-widest text-teal-deep font-medium mb-4">
                Somos a solução
              </span>
              <div className="relative">
              <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-teal/15" />
              <img
                src={examesOrganizados}
                alt="Profissional de saúde organizando exames em uma mesa"
                width={1408}
                height={1008}
                loading="lazy"
                className="relative rounded-3xl shadow-lift w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-4 md:-left-8 bg-card rounded-2xl shadow-lift p-5 max-w-[260px] border border-border">
                <div className="flex items-center gap-2 text-teal-deep mb-2">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wider font-medium">ACOLHIMENTO</span>
                </div>
                <p className="text-sm text-ink leading-snug">
                  Reunimos exames, guias de autorizações e orientações em um único painel de acompanhamento.
                </p>
              </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 bg-mist/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14 space-y-4 max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-teal-deep">
                Por que a preparação importa
              </span>
              <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
                O verdadeiro risco de uma preparação incompleta
              </h2>
            </div>

            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
              <div className="relative">
                <div className="absolute -top-5 -left-5 h-20 w-20 rounded-full bg-teal/20" aria-hidden />
                <div className="absolute top-1/3 -right-8 h-32 w-32 rounded-full bg-teal-soft/25" aria-hidden />
                <div className="absolute -bottom-4 left-1/4 h-14 w-14 rounded-full bg-navy/10" aria-hidden />
                <img
                  src={fotoCancelamento}
                  alt="Paciente emocionada à porta do centro cirúrgico, acolhida por familiar e equipe médica"
                  width={832}
                  height={1248}
                  loading="lazy"
                  className="relative rounded-3xl shadow-lift w-full h-auto object-cover aspect-[3/4]"
                />
              </div>

              <div className="grid gap-4">
                {riscosPreparacao.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="group rounded-2xl bg-card border border-border p-6 transition-colors shadow-soft hover:border-red-300 hover:bg-red-50/80"
                  >
                    <div className="h-10 w-10 rounded-xl bg-teal/10 text-teal-deep flex items-center justify-center mb-4 transition-colors group-hover:bg-red-100 group-hover:text-red-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-medium text-navy text-lg mb-2 transition-colors group-hover:text-red-900">
                      {title}
                    </h3>
                    <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <ChecklistProntidao />

        <section className="py-24 px-6 bg-navy text-cream relative overflow-hidden">
          <div className="absolute inset-0 bg-radial-mist opacity-40" />
          <div className="max-w-7xl mx-auto relative">
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center mb-16">
              <div>
                <span className="text-xs uppercase tracking-widest text-teal-soft font-medium">
                  O que a consultoria entrega
                </span>
                <h2 className="font-bold text-3xl md:text-5xl mt-3 leading-tight">
                  Mais que resposta genérica —{' '}
                  <em className="text-teal-soft not-italic">orientação humana</em> antes da cirurgia
                </h2>
                <p className="text-cream/70 text-lg mt-5 max-w-xl">
                  Transformamos ansiedade e confusão em um plano prático que você entende e
                  consegue executar.
                </p>
              </div>
              <div className="relative">
                <img
                  src={concierge}
                  alt="Concierge de saúde sorrindo enquanto atende paciente"
                  width={1408}
                  height={1008}
                  loading="lazy"
                  className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-lift"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {beneficios.map(({ icon: Icon, title, description, bullets }) => (
                <div
                  key={title}
                  className="rounded-3xl bg-cream/5 backdrop-blur border border-cream/10 p-7 hover:bg-cream/10 transition-colors"
                >
                  <div className="h-12 w-12 rounded-2xl bg-teal-soft/20 text-teal-soft flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">{title}</h3>
                  <p className="text-cream/70 text-sm mb-5 leading-relaxed">{description}</p>
                  <ul className="space-y-2 text-sm">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-cream/85">
                        <CheckCircle2 className="h-4 w-4 text-teal-soft shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 space-y-3">
              <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
                Como funciona
              </span>
              <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
                Uma jornada simples até a clareza
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-teal to-transparent" />
              {passos.map(({ step, title, description }) => (
                <div key={step} className="relative text-center">
                  <div className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-card border-2 border-teal font-bold text-xl text-teal-deep mb-6 shadow-soft">
                    {step}
                  </div>
                  <h3 className="font-medium text-navy text-lg mb-2">{title}</h3>
                  <p className="text-ink-soft text-sm leading-relaxed max-w-xs mx-auto">
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 px-6 bg-mist/30">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 space-y-3">
              <span className="text-xs uppercase tracking-widest text-teal-deep font-medium">
                Perguntas frequentes
              </span>
              <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
                Tudo o que você precisa saber
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq) => (
                <AccordionItem
                  key={faq.value}
                  value={faq.value}
                  className="rounded-2xl border border-border bg-card px-5 shadow-soft"
                >
                  <AccordionTrigger className="text-left font-medium text-navy py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-soft leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="relative py-32 px-6 overflow-hidden">
          <img
            src={ctaAcolhimento}
            alt="Familiar segurando a mão do paciente em quarto hospitalar acolhedor"
            width={1600}
            height={1008}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/70" />
          <div className="relative max-w-3xl mx-auto text-center text-cream space-y-8">
            <h2 className="font-bold text-3xl md:text-5xl leading-tight">
              Quanto antes você entende o que falta,{' '}
              <em className="text-teal-soft not-italic">menos espaço sobra para imprevistos</em>
            </h2>
            <p className="text-cream/85 text-lg max-w-2xl mx-auto">
              Comece pelo checklist gratuito. Se quiser apoio humano, fale com o concierge para
              revisar seu caso com profundidade.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={handleFinalChecklistClick}
                className="bg-teal hover:bg-teal-deep text-white h-14 px-8 text-base rounded-full shadow-lift"
              >
                Fazer meu checklist agora
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleFinalWhatsAppClick}
                className="h-14 px-8 text-base rounded-full bg-cream/5 backdrop-blur border-cream/25 text-cream hover:bg-cream/15 hover:text-cream"
              >
                Falar com o concierge
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ConsultoriaPreCirurgica
